
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useCreateExamQuestion } from '@/hooks/useExamQuestions';

interface CreateExamQuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
}

export function CreateExamQuestionDialog({ isOpen, onClose, courseId }: CreateExamQuestionDialogProps) {
  const [formData, setFormData] = useState({
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    correct_answer: 'A',
    module_number: 1,
    part_number: 1,
    language: 'English',
    is_active: true
  });

  const createQuestion = useCreateExamQuestion();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const questionData = {
      ...formData,
      course_id: courseId,
      option_c: formData.option_c || null
    };

    createQuestion.mutate(questionData, {
      onSuccess: () => {
        onClose();
        setFormData({
          question_text: '',
          option_a: '',
          option_b: '',
          option_c: '',
          correct_answer: 'A',
          module_number: 1,
          part_number: 1,
          language: 'English',
          is_active: true
        });
      }
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Exam Question</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="module">Module Number</Label>
              <Input
                id="module"
                type="number"
                min="0"
                value={formData.module_number}
                onChange={(e) => setFormData({ ...formData, module_number: parseInt(e.target.value) || 1 })}
                required
              />
            </div>
            <div>
              <Label htmlFor="part">Part Number</Label>
              <Input
                id="part"
                type="number"
                min="1"
                value={formData.part_number}
                onChange={(e) => setFormData({ ...formData, part_number: parseInt(e.target.value) || 1 })}
                required
              />
            </div>
            <div>
              <Label htmlFor="language">Language</Label>
              <Select
                value={formData.language}
                onValueChange={(value) => setFormData({ ...formData, language: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Spanish">Spanish</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="question">Question Text</Label>
            <Textarea
              id="question"
              value={formData.question_text}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              required
              rows={3}
            />
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="option_a">Option A</Label>
              <Input
                id="option_a"
                value={formData.option_a}
                onChange={(e) => setFormData({ ...formData, option_a: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="option_b">Option B</Label>
              <Input
                id="option_b"
                value={formData.option_b}
                onChange={(e) => setFormData({ ...formData, option_b: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="option_c">Option C (Optional)</Label>
              <Input
                id="option_c"
                value={formData.option_c}
                onChange={(e) => setFormData({ ...formData, option_c: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="correct_answer">Correct Answer</Label>
            <Select
              value={formData.correct_answer}
              onValueChange={(value) => setFormData({ ...formData, correct_answer: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="A">A</SelectItem>
                <SelectItem value="B">B</SelectItem>
                {formData.option_c && <SelectItem value="C">C</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
            <Label htmlFor="is_active">Active</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={createQuestion.isPending}>
              {createQuestion.isPending ? 'Creating...' : 'Create Question'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
